import React from "react"

import {TaskCardItem} from "../../components/TaskCard/types"
import {FeatureItem} from "../../components/FeatureItemCard/types"
import {ProgramPerformanceItem} from "../../components/ProgramPerformanceItemCard/types"
import classNames from "classnames"
import {GettingStartedCard} from "../../components/ReadyToLaunchHomepage/GettingStartedCard/GettingStartedCard"
import {ProgramPerformanceCard} from "../../components/ReadyToLaunchHomepage/ProgramPerformanceCard/ProgramPerformanceCard"
import {FeaturesCard} from "../../components/ReadyToLaunchHomepage/FeaturesCard/FeaturesCard"
import {useAccessToken} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import Loading from "../../components/Loading/Loading"
import {getReadyToLaunchStatus, GettingStartedAttributes} from "../../resources/readyToLaunchStatus"
import {featuresItems} from "../../components/ReadyToLaunchHomepage/FeaturesCard/data"
import {
	getPerformanceItems,
	PerformanceKey,
} from "../../components/ReadyToLaunchHomepage/ProgramPerformanceCard/getPerformanceItems"
import {findCustomers} from "../../resources/customer"
import {Account, findAccountsHandler, getAccountsMetrics} from "../../resources/account"
import {OkDocument} from "../../resources/common"
import {getCustomerCards} from "../../resources/card"
import {useModal} from "react-modal-hook"
import {CopyCodeModal} from "../../components/CopyCodeModal/CopyCodeModal"
import {useNavigate} from "react-router-dom"
import {NavigateFunction} from "react-router/dist/lib/hooks"

const getGettingStartedTasks = ({
	attributes,
	showCopyCodeModal,
	navigate,
}: {
	attributes?: GettingStartedAttributes
	showCopyCodeModal: () => void
	navigate: NavigateFunction
}): TaskCardItem[] => {
	const {setupAuthentication, embedTheComponent, setupPrefill} = attributes ?? {}
	return [
		{
			title: "Review the docs",
			description: "Read our simple guide to understand the implementation process",
			duration: "10 minutes",
			icon: "programming-book",
			isDone: setupAuthentication || embedTheComponent || setupPrefill,
			url: "https://unit.co/docs/ready-to-launch",
		},
		{
			title: "Set up end-user authentication",
			description: "Configure your Identity provider to allow for seamless authentication",
			duration: "15 minutes",
			icon: "lock-square-lock-1--interface-essential",
			isDone: setupAuthentication,
			onClick: () => {
				navigate("/ready-to-launch/settings")
			},
		},
		{
			title: "Embed the Ready to Launch component into your app",
			description: "Read the getting started guide",
			duration: "30 minutes",
			icon: "programming-code-terminal-2",
			isDone: embedTheComponent,
			onClick: showCopyCodeModal,
		},
		{
			title: "Set up your callback endpoints (optional)",
			description: "Streamline the end user experience by pre-filling information you already have",
			duration: "1-2 days",
			icon: "checklist-pen--interface-essential",
			isDone: setupPrefill,
			onClick: () => {
				navigate("/ready-to-launch/settings#tab=Callbacks")
			},
		},
	]
}

interface ReadyToLaunchHomepageProps {
	className?: string
	tasks: TaskCardItem[]
	performanceItems: ProgramPerformanceItem[]
	features: FeatureItem[]
}

export function ReadyToLaunchHomepageView({tasks, performanceItems, features, className}: ReadyToLaunchHomepageProps) {
	return (
		<div className={classNames("ready-to-launch-homepage", className)}>
			<div className="ready-to-launch-homepage-content">
				<h2 className="homepage-title">Welcome to Ready-to-Launch</h2>
				<GettingStartedCard className="getting-started-card" tasks={tasks} />
				<ProgramPerformanceCard className="program-performance-card" items={performanceItems} />
				<FeaturesCard features={features} />
			</div>
		</div>
	)
}

export function ReadyToLaunchHomepage() {
	const accessToken = useAccessToken()
	const status = useAsyncResult(() => getReadyToLaunchStatus(accessToken))
	const customers = useAsyncResult(() => findCustomers(accessToken, 0, 1000, "-createdAt", ""))
	const accounts = useAsyncResult(() =>
		findAccountsHandler({
			accessToken,
			offset: 0,
			types: [],
			requestType: "json",
		})
	)
	const cards = useAsyncResult(() => getCustomerCards(accessToken, 0, 1000))
	const accountMetrics = useAsyncResult(() => getAccountsMetrics(accessToken))
	const [showCopyCodeModal, hideCopyCodeModal] = useModal(() => <CopyCodeModal close={hideCopyCodeModal} />)
	const navigate = useNavigate()

	return AsyncResult.zip5(status, customers, accounts, cards, accountMetrics).match(
		() => <Loading />,
		([status, customers, accounts, cards, accountMetrics]) => {
			const gettingStartedTasks = getGettingStartedTasks({
				attributes: status.data.attributes,
				showCopyCodeModal,
				navigate,
			})
			const performanceData: Record<PerformanceKey, string> = {
				customers: customers.meta?.pagination.total.toString() ?? "N/A",
				accounts: (accounts as OkDocument<Account[]>).meta?.pagination.total.toString() ?? "N/A",
				depositBalance: accountMetrics.data.attributes.totalDepositBalance.toString(),
				cards: cards.meta?.pagination.total.toString() ?? "N/A",
			}
			return (
				<ReadyToLaunchHomepageView
					tasks={gettingStartedTasks}
					performanceItems={getPerformanceItems(performanceData)}
					features={featuresItems}
				/>
			)
		},
		(_) => null
	)
}
